import React, {
  useState,
  useEffect,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from "react";
import { Box, Typography, TextField, Switch } from "@mui/material";
import { themes } from "services/constants";
import { CustomButton } from "components/CustomComponents";
import { CustomAutoComplete, CustomTextBox } from "components/CustomComponents";
import { useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { useFetch } from "services/hooks/useFetch";
import { onlyNumberRegex } from "services/constants/patterns";
import { ShiftAssignmentForShift } from "../../ShiftRepresentation/ShiftAssignmentForShift";
import { ShiftAssignmentForWeekDays } from "../../ShiftRepresentation/ShiftAssignmentForWeekDays";
import {
  weekdays,
  daysDetails,
} from "pages/Administration/AdministrationHeaders";
import dayjs from "dayjs";
import { useShiftRota } from "../../ShiftRotaContext";

export const CloneShiftRotaSchedule = forwardRef(
  ({ handleNext, handleBack, onUpdate, showFullPage }) => {
    const {
      configRef,
      formData,
      WeekDaysData,
      setWeekDaysData,
      allGroupDetails,
      shiftCycleDetails,
      setShiftCycleDetails,
      generateShiftMatrics,
    } = useShiftRota();
    const {
      setValue,
      register,
      watch,
      reset,
      handleSubmit,
      getValues,
      resetField,
      clearErrors,
      setError,
      formState: { errors },
    } = useForm({
      mode: "onBlur",
      defaultValues: {
        ...formData?.schedule,
      },
    });

    const { domain } = useSelector((state) => state.tokenReducer);
    const [rotationDates, setRotationDates] = useState([]);
    const [updateData, setUpdateData] = useState(true);
    const [rotationDay, setRotationDay] = useState("");
    const [rotationDate, setRotationDate] = useState("");
    const [firstRotationDateDate, setFirstRotationDate] = useState("");
    const [weekdayError, setWeekDayError] = useState(false);
    const [shiftError, setShiftError] = useState(false);
    const [autoSftAssignData, setAutoSftAssignData] = useState("");
    const [rotationFrequency, setRotationFrequency] = useState("");
    const [rotationBasedOn, setRotationBasedOn] = useState("");
    const [continueDetectionUntilShiftEnd, setContinueDetectionUntilShiftEnd] =
      useState(false);

    let effective_from = formData?.config?.effective_from;
    let rotation_frequency = watch("rotation_frequency");
    let duration_for_one_cycle = watch("duration_for_one_cycle");
    let rotation_month_day = watch("rotation_month_day");
    let rotation_day_week = watch("rotation_day_week");

    const getRotationDates = useCallback(() => {
      let dates = [];
      let startDate = dayjs(effective_from);
      if (rotation_frequency === "Month") {
        for (let i = 1; i <= duration_for_one_cycle; i++) {
          let nextDate = startDate?.add(i, "month")?.date(rotation_month_day);
          dates?.push(nextDate?.format("DD/MM/YYYY"));
        }
      } else if (rotation_frequency === "Week") {
        let selectedDayIndex = weekdays.findIndex(
          (day) => day.label === rotation_day_week,
        );
        let nextMonday = startDate.day(
          selectedDayIndex >= startDate.day()
            ? selectedDayIndex + 1
            : selectedDayIndex + 8,
        );

        for (let i = 0; i < duration_for_one_cycle; i++) {
          dates.push(nextMonday.add(i * 7, "day").format("DD/MM/YYYY"));
        }
      }
      return dates;
    }, [
      duration_for_one_cycle,
      effective_from,
      rotation_day_week,
      rotation_frequency,
      rotation_month_day,
    ]);
    const rotation = [
      {
        label: "Month",
      },
      {
        label: "Week",
      },
    ];

    const AutoSftData = [
      {
        label: "Yes",
      },
      {
        label: "No",
      },
    ];
    const { result: sftFieldData } = useFetch(
      `${domain}administration/shiftroster/shiftgroups/shift-choices/`,
    );

    useEffect(() => {
      if (updateData && formData?.schedule) {
        if (!formData?.schedule?.already_created) {
          reset({
            ...formData?.schedule,
            rotation_day_week: "",
            rotation_month_day: "",
            duration_for_one_cycle: "",
            first_rotation_date: "",
          });
        } else {
          reset(formData?.schedule);
          setRotationDate(formData?.schedule?.rotation_month_day);
          setRotationDay(formData?.schedule?.rotation_day_week);
          setFirstRotationDate(formData?.schedule?.first_rotation_date);
        }
        setAutoSftAssignData(formData?.schedule?.auto_sft_assign);
        setContinueDetectionUntilShiftEnd(
          Boolean(formData?.schedule?.is_shift_full_time)
        );
        setValue(
          "start_buffer",
          formData?.schedule?.start_buffer ??
            formData?.schedule?.auto_shift_before_start ??
            "60"
        );
        setValue(
          "end_buffer",
          formData?.schedule?.end_buffer ??
            formData?.schedule?.auto_shift_after_start ??
            "90"
        );
        setValue(
          "is_shift_full_time",
          Boolean(formData?.schedule?.is_shift_full_time)
        );
        setRotationFrequency(formData?.schedule?.rotation_frequency);
        setRotationBasedOn(formData?.schedule?.rotation_based_on);
      }
    }, [
      formData?.schedule,
      getValues,
      reset,
      resetField,
      setValue,
      updateData,
      watch,
    ]);

    const updateRotationFrequency = (event, data) => {
      setValue("rotation_frequency", data?.label || "");
      setRotationFrequency(data?.label);
      setValue("rotation_day_week", "");
      setValue("rotation_month_day", "");
      setValue("rotation_month_day", "");
      setValue("duration_for_one_cycle", "");
      setFirstRotationDate("");
      setValue("first_rotation_date", "");
      setRotationDates([]);
      setRotationDay("");
      setRotationDate("");
    };

    const selectWeekDay = (event, data) => {
      setValue("rotation_day_week", data?.label || "");
      setRotationDay(data?.label);
      setFirstRotationDate("");
      setValue("first_rotation_date", "");
    };

    const selectMonthDay = (event, data) => {
      setValue("rotation_month_day", data?.label || "");
      setRotationDate(data?.label);
      setFirstRotationDate("");
      setValue("first_rotation_date", "");
    };

    const updateShiftAssignmentDuration = (event) => {
      if (
        onlyNumberRegex.test(event.target.value) &&
        event.target.value.length <= 3
      ) {
        setValue("duration_for_one_cycle", event.target.value);
        setFirstRotationDate("");
        setValue("first_rotation_date", "");
      }
    };

    useEffect(() => {
      if (
        formData?.config?.effective_from &&
        rotation_frequency &&
        duration_for_one_cycle &&
        (rotation_day_week || rotation_month_day)
      ) {
        const dates = getRotationDates();
        setRotationDates(dates?.map((value) => ({ label: value })));
      }
    }, [
      duration_for_one_cycle,
      effective_from,
      formData?.config?.effective_from,
      getRotationDates,
      rotation_day_week,
      rotation_frequency,
      rotation_month_day,
    ]);

    const handleFirstRotationDate = (event, data) => {
      if (data) {
        setValue("first_rotation_date", data?.label);
        setFirstRotationDate(data?.label);
      }
    };

    const updateAutoSftAssign = (event, data) => {
      if (data) {
        setValue("auto_sft_assign", data?.label);
        setAutoSftAssignData(data?.label);
        if (data?.label === "Yes") {
          setValue(
            "is_shift_full_time",
            Boolean(getValues("is_shift_full_time"))
          );
          setValue("start_buffer", getValues("start_buffer") || "60");
          setValue("end_buffer", getValues("end_buffer") || "90");
          setContinueDetectionUntilShiftEnd(
            Boolean(getValues("is_shift_full_time"))
          );
        } else {
          setValue("is_shift_full_time", false);
          setValue("start_buffer", "");
          setValue("end_buffer", "");
          setContinueDetectionUntilShiftEnd(false);
          clearErrors(["is_shift_full_time", "start_buffer", "end_buffer"]);
        }
      }
    };

    const updateAutoShiftWindow = (fieldName, value) => {
      if (onlyNumberRegex.test(value) && value.length <= 3) {
        setValue(fieldName, value);
        if (value) clearErrors(fieldName);
      }
    };

    const validateAutoShiftDetectionFields = () => {
      if (getValues("auto_sft_assign") !== "Yes") return true;

      let isValid = true;
      const beforeShiftStart = getValues("start_buffer");
      const afterShiftStart = getValues("end_buffer");

      if (!beforeShiftStart) {
        setError("start_buffer", {
          type: "required",
          message: "Before Shift Start is required",
        });
        isValid = false;
      }

      if (!afterShiftStart) {
        setError("end_buffer", {
          type: "required",
          message: "After Shift Start is required",
        });
        isValid = false;
      }

      return isValid;
    };

    const updateContinueDetectionUntilShiftEnd = (event) => {
      const nextValue = event.target.checked;
      setContinueDetectionUntilShiftEnd(nextValue);
      setValue("is_shift_full_time", nextValue);
    };

    const autoShiftBeforeStartValue = watch("start_buffer");
    const autoShiftAfterStartValue = watch("end_buffer");
    const timelineBeforeShiftStart =
      autoShiftBeforeStartValue === ""
        ? "0"
        : autoShiftBeforeStartValue || "60";
    const timelineAfterShiftStart =
      autoShiftAfterStartValue === ""
        ? "0"
        : autoShiftAfterStartValue || "90";

    const validateWeekDaysShifts = () => {
      let unselectedShifts = WeekDaysData.filter((data) =>
        data.cycles.some((cycData) =>
          cycData.assignments.some((assignment) => assignment.shift_id === ""),
        ),
      );
      if (unselectedShifts.length === 0) {
        setWeekDayError(false);
        return true;
      } else {
        setWeekDayError(true);
        return false;
      }
    };

    const validateShifts = () => {
      let unselectedShifts = shiftCycleDetails.filter((data) =>
        data.assignments.some((assignment) => assignment.shift_id === ""),
      );
      if (unselectedShifts.length === 0) {
        setShiftError(false);
        return true;
      } else {
        setShiftError(true);
        return false;
      }
    };

    useImperativeHandle(configRef, () => ({
      saveFormValues: () => {
        const currentValues = getValues();
        onUpdate({
          ...currentValues,
        });
      },
    }));

    const onSubmit = async (formValues) => {
      const isAutoShiftDetectionValid = validateAutoShiftDetectionFields();
      const isValid =
        watch("rotation_based_on") === "Weekdays"
          ? validateWeekDaysShifts()
          : validateShifts();
      if (!isAutoShiftDetectionValid || !isValid) {
        return;
      }
      onUpdate({
        ...formValues,
        already_created: true,
        callRepresentationApi: true,
      });
      handleNext(2);
    };

    const updateRotationBasedOn = (event, data) => {
      if (data) {
        setValue("rotation_based_on", data?.label);
        setRotationBasedOn(data?.label);
        generateShiftMatrics(
          formData?.config?.shiftGroup,
          formData?.config?.shift,
        );
        if (data?.label === "Weekdays") {
          setRotationFrequency("Week");
          if (getValues("rotation_based_on") === "Weekdays") {
            setValue("rotation_frequency", "Week");
            setValue("rotation_frequency", "Week");
          }
        } else {
          setRotationFrequency("");
        }
      } else {
        setRotationFrequency("");
      }
    };

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{ flexGrow: 1, overflow: "auto", p: 2 }}
          component="form"
          onSubmit={handleSubmit(onSubmit)}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2.5,
            }}
          >
            <Box
              sx={{
                ml: 1,
              }}
            >
              <Typography sx={{ fontWeight: 600 }}>Shift</Typography>
              <Typography variant="body2" color="text.secondary">
                Choose shifts to include in this rota
              </Typography>
            </Box>
            <Box>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 4,
                  ml: 1,
                }}
              >
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Rotation Based On
                    <span style={{ color: themes?.redColor }}>*</span>
                  </Typography>
                  <CustomAutoComplete
                    dataList={
                      sftFieldData?.rotation_based_on_choices ||
                      []?.map((obj) => ({
                        ...obj,
                        label: obj?.label,
                      }))
                    }
                    selectedValue={rotationBasedOn}
                    rules={{
                      ...register("rotation_based_on", {
                        required: "Shift is required",
                      }),
                    }}
                    updateDataFunction={updateRotationBasedOn}
                    placeHolder="Select the rotation frequency"
                    errorMessage={
                      errors?.rotation_based_on?.message
                        ? errors?.rotation_based_on?.message
                        : ""
                    }
                    textBoxError={errors?.rotation_based_on ? true : false}
                  />
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Rotation Frequency
                    <span style={{ color: themes?.redColor }}>*</span>
                  </Typography>
                  {getValues("rotation_based_on") === "Weekdays" ? (
                    <TextField
                      value={rotationFrequency}
                      size="small"
                      fullWidth
                    />
                  ) : (
                    <CustomAutoComplete
                      dataList={rotation?.map((obj) => ({
                        ...obj,
                        label: obj?.label,
                      }))}
                      selectedValue={rotationFrequency}
                      rules={{
                        ...register("rotation_frequency", {
                          required: "Rotation frequency is required",
                        }),
                      }}
                      updateDataFunction={updateRotationFrequency}
                      placeHolder="Select the rotation frequency"
                      errorMessage={
                        errors?.rotation_frequency?.message
                          ? errors?.rotation_frequency?.message
                          : ""
                      }
                      textBoxError={errors?.rotation_frequency ? true : false}
                    />
                  )}
                </Box>
              </Box>
            </Box>
            <Box>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 4,
                  ml: 1,
                }}
              >
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Rotation Day
                    <span style={{ color: themes?.redColor }}>*</span>
                  </Typography>

                  {getValues("rotation_frequency") === "Week" ? (
                    <CustomAutoComplete
                      dataList={weekdays}
                      selectedValue={rotationDay}
                      rules={{
                        ...register("rotation_day_week", {
                          required: false,
                          validate: {
                            minLengthNoSpaces: (value) =>
                              getValues("rotation_frequency") === "Week" &&
                              !value
                                ? "Rotation day is required"
                                : true,
                          },
                        }),
                      }}
                      updateDataFunction={selectWeekDay}
                      placeHolder="Select the rotation day"
                      errorMessage={
                        errors?.rotation_day_week?.message
                          ? errors?.rotation_day_week?.message
                          : ""
                      }
                      textBoxError={errors?.rotation_day_week ? true : false}
                    />
                  ) : (
                    <CustomAutoComplete
                      dataList={daysDetails}
                      selectedValue={rotationDate}
                      rules={{
                        ...register("rotation_month_day", {
                          required: false,
                          validate: {
                            minLengthNoSpaces: (value) =>
                              getValues("rotation_frequency") === "Month" &&
                              !value
                                ? "Rotation month day is required"
                                : true,
                          },
                        }),
                      }}
                      updateDataFunction={selectMonthDay}
                      placeHolder="Select the rotation month day"
                      errorMessage={
                        errors?.rotation_month_day?.message
                          ? errors?.rotation_month_day?.message
                          : ""
                      }
                      textBoxError={errors?.rotation_month_day ? true : false}
                    />
                  )}
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    No of Repetition
                    <span style={{ color: themes?.redColor }}>*</span>
                  </Typography>
                  <CustomTextBox
                    selectedValue={watch("duration_for_one_cycle")}
                    rules={{
                      ...register("duration_for_one_cycle", {
                        required: "Cycle duration is required",
                      }),
                    }}
                    updateDataFunction={updateShiftAssignmentDuration}
                    placeHolder="Enter cycle duration"
                    errorMessage={
                      errors?.duration_for_one_cycle?.message
                        ? errors?.duration_for_one_cycle?.message
                        : ""
                    }
                    textBoxError={errors.duration_for_one_cycle ? true : false}
                  />
                </Box>
              </Box>
            </Box>
            <Box>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 4,
                  ml: 1,
                }}
              >
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    First Rotation Date
                    <span style={{ color: themes?.redColor }}>*</span>
                  </Typography>
                  <CustomAutoComplete
                    dataList={rotationDates}
                    selectedValue={firstRotationDateDate}
                    rules={{
                      ...register("first_rotation_date", {
                        required: "First Rotation Date is required",
                      }),
                    }}
                    updateDataFunction={handleFirstRotationDate}
                    placeHolder="Select the first rotation date"
                    errorMessage={
                      errors?.first_rotation_date?.message
                        ? errors?.first_rotation_date?.message
                        : ""
                    }
                    textBoxError={errors?.first_rotation_date ? true : false}
                  />
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Auto Shift Assign
                    <span style={{ color: themes?.redColor }}>*</span>
                  </Typography>
                  <CustomAutoComplete
                    dataList={AutoSftData?.map((obj) => ({
                      ...obj,
                      label: obj?.label,
                    }))}
                    selectedValue={autoSftAssignData}
                    rules={{
                      ...register("auto_sft_assign", {
                        required: "Auto shift assign is required",
                      }),
                    }}
                    updateDataFunction={updateAutoSftAssign}
                    placeHolder="Select the rotation frequency"
                    errorMessage={
                      errors?.auto_sft_assign?.message
                        ? errors?.auto_sft_assign?.message
                        : ""
                    }
                    textBoxError={errors?.auto_sft_assign ? true : false}
                  />
                </Box>
              </Box>
            </Box>
            {autoSftAssignData === "Yes" ? (
              <Box sx={{ ml: 1 }}>
                <Box
                  sx={{
                    pt: 2.5,
                    borderTop: `1px solid ${themes?.miliedLightGray}`,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Box>
                      <Typography
                        sx={{
                          fontWeight: 600,
                          color: themes?.textBlack,
                        }}
                      >
                        Auto Shift Detection (First IN Punch)
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.75 }}
                      >
                        Define how the first IN punch will be matched to a shift.
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ mt: 4 }}>
                    <Typography sx={{ fontWeight: 600, mb: 2.5 }}>
                      Detection Window
                    </Typography>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", md: "440px 1fr" },
                        rowGap: 2.5,
                        columnGap: 4,
                        alignItems: "center",
                      }}
                    >
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "170px 140px auto",
                          gap: 2,
                          alignItems: "center",
                        }}
                      >
                        <Typography sx={{ fontWeight: 600 }}>
                          Before Shift Start
                        </Typography>
                        <TextField
                          value={watch("start_buffer") || ""}
                          onChange={(event) =>
                            updateAutoShiftWindow("start_buffer", event.target.value)
                          }
                          size="small"
                          error={Boolean(errors?.start_buffer)}
                          helperText={errors?.start_buffer?.message || ""}
                        />
                        <Typography variant="body2">minutes</Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        How early before shift start the punch can be detected.
                      </Typography>

                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "170px 140px auto",
                          gap: 2,
                          alignItems: "center",
                        }}
                      >
                        <Typography sx={{ fontWeight: 600 }}>
                          After Shift Start
                        </Typography>
                        <TextField
                          value={watch("end_buffer") || ""}
                          onChange={(event) =>
                            updateAutoShiftWindow("end_buffer", event.target.value)
                          }
                          size="small"
                          error={Boolean(errors?.end_buffer)}
                          helperText={errors?.end_buffer?.message || ""}
                        />
                        <Typography variant="body2">minutes</Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        How late after shift start the punch can be detected.
                      </Typography>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      mt: 3,
                      pt: 2.5,
                      borderTop: `1px solid ${themes?.miliedLightGray}`,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                        <Switch
                          checked={continueDetectionUntilShiftEnd}
                          onChange={updateContinueDetectionUntilShiftEnd}
                        />
                        <Box>
                          <Typography sx={{ fontWeight: 600 }}>
                            Continue Detection Until Shift End
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 0.75, maxWidth: 620 }}
                          >
                            When enabled, if the first IN punch occurs anywhere
                            between Shift Start and Shift End, the employee will
                            still be assigned to this shift.
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      mt: 3,
                      border: `1px solid ${themes?.miliedLightGray}`,
                      borderRadius: 2,
                      p: { xs: 2, sm: 2.5 },
                      backgroundColor: themes?.whiteColor,
                    }}
                  >
                    <Typography sx={{ fontWeight: 600, mb: 2.5 }}>
                      Timeline Preview
                    </Typography>
                    <Box
                      sx={{
                        position: "relative",
                        height: { xs: 220, sm: 220 },
                        overflow: "hidden",
                      }}
                    >
                      <Typography
                        sx={{
                          position: "absolute",
                          left: showFullPage ? "8%" : "4%",
                          top: 8,
                          color: "#3b82f6",
                          fontWeight: 700,
                          fontSize: showFullPage ? "1rem" : "0.96rem",
                        }}
                      >
                        {timelineBeforeShiftStart + " min before"}
                      </Typography>
                      <Typography
                        sx={{
                          position: "absolute",
                          left: showFullPage ? "31%" : "28%",
                          top: 8,
                          color: themes?.textBlack,
                          fontWeight: 700,
                          fontSize: showFullPage ? "1rem" : "0.96rem",
                        }}
                      >
                        Shift Start
                      </Typography>
                      <Typography
                        sx={{
                          position: "absolute",
                          left: showFullPage ? "55%" : "52%",
                          top: 8,
                          color: showFullPage ? "#34c38f" : "#58b894",
                          fontWeight: 700,
                          fontSize: showFullPage ? "1rem" : "0.96rem",
                        }}
                      >
                        {timelineAfterShiftStart + " min after"}
                      </Typography>
                      <Typography
                        sx={{
                          position: "absolute",
                          right: showFullPage ? "13%" : "8%",
                          top: 8,
                          color: themes?.textBlack,
                          fontWeight: 700,
                          fontSize: showFullPage ? "1rem" : "0.96rem",
                        }}
                      >
                        Shift End
                      </Typography>
                      <Box
                        sx={{
                          position: "absolute",
                          left: "4%",
                          right: "4%",
                          top: showFullPage ? 70 : 58,
                          borderTop: "3px dashed #a7b0bf",
                        }}
                      />
                      <Box
                        sx={{
                          position: "absolute",
                          left: continueDetectionUntilShiftEnd
                            ? "14%"
                            : "calc(12% + 9px)",
                          ...(continueDetectionUntilShiftEnd
                            ? {
                                right: showFullPage ? "15%" : "12%",
                              }
                            : {
                                width: "46%",
                              }),
                          top: showFullPage ? 70 : 58,
                          borderTop: `3px solid ${
                            continueDetectionUntilShiftEnd
                              ? "#34c38f"
                              : "#3b82f6"
                          }`,
                        }}
                      />
                      <Box sx={{ position: "absolute", left: "12%", top: showFullPage ? 61 : 49, width: 18, height: 18, borderRadius: "50%", backgroundColor: "#2563eb" }} />
                      <Box sx={{ position: "absolute", left: "34%", top: showFullPage ? 61 : 49, width: 18, height: 18, borderRadius: "50%", backgroundColor: themes?.textBlack || "#111827" }} />
                      <Box sx={{ position: "absolute", left: "58%", top: showFullPage ? 61 : 49, width: 18, height: 18, borderRadius: "50%", backgroundColor: themes?.successColor || "#10b981" }} />
                      <Box sx={{ position: "absolute", right: "15%", top: showFullPage ? 61 : 49, width: 18, height: 18, borderRadius: "50%", backgroundColor: "#7c3aed" }} />
                      <Box sx={{ position: "absolute", left: "12%", width: showFullPage ? "44%" : "46%", top: showFullPage ? 96 : 82, height: showFullPage ? 26 : 22, borderLeft: "2px solid #60a5fa", borderRight: "2px solid #60a5fa", borderBottom: "2px solid #60a5fa", borderBottomLeftRadius: 12, borderBottomRightRadius: 12, opacity: 0.8 }} />
                      <Box sx={{ position: "absolute", left: showFullPage ? "34%" : "36%", right: showFullPage ? "15%" : "14%", top: showFullPage ? 146 : 140, height: showFullPage ? 22 : 18, borderLeft: showFullPage ? "2px solid #10b981" : "2px solid #34c38f", borderRight: showFullPage ? "2px solid #10b981" : "2px solid #34c38f", borderBottom: showFullPage ? "2px solid #10b981" : "2px solid #34c38f", borderBottomLeftRadius: 12, borderBottomRightRadius: 12, opacity: continueDetectionUntilShiftEnd ? 0.95 : 0.55 }} />
                      <Typography sx={{ position: "absolute", left: showFullPage ? "15%" : "10%", top: showFullPage ? 125 : 112, color: "#2563eb", fontWeight: 700, textAlign: "center", fontSize: showFullPage ? "1rem" : "0.82rem", lineHeight: showFullPage ? 1.3 : 1.2 }}>
                        Preferred Detection Window
                        <br />
                        (Always Active)
                      </Typography>
                      <Typography sx={{ position: "absolute", left: showFullPage ? "45%" : "40%", right: showFullPage ? "12%" : "6%", top: showFullPage ? 170 : 166, color: showFullPage ? "#10b981" : "#34c38f", fontWeight: 700, textAlign: "center", opacity: continueDetectionUntilShiftEnd ? 1 : 0.72, fontSize: showFullPage ? "1rem" : "0.8rem", lineHeight: showFullPage ? 1.25 : 1.15 }}>
                        Continue Detection Until Shift End (Optional)
                        <br />
                        Active when enabled
                      </Typography>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      mt: 2,
                      p: 2.5,
                      borderRadius: 2,
                      backgroundColor: "#eef4ff",
                      border: "1px solid #d6e4ff",
                    }}
                  >
                    <Typography sx={{ fontWeight: 600, mb: 1.25 }}>
                      About Auto Shift Detection
                    </Typography>
                    <Typography component="div" variant="body2">
                      <ul style={{ margin: 0, paddingLeft: 20 }}>
                        <li>This setting applies only to the first IN punch of the day.</li>
                        <li>All subsequent punches (OUT, breaks, etc.) will remain under the assigned shift.</li>
                      </ul>
                    </Typography>
                  </Box>
                </Box>
              </Box>
            ) : null}
            <Box
              sx={{
                borderBottom: `1px solid ${themes?.miliedLightGray}`,
                borderRadius: 0,
              }}
            ></Box>
            <Box
              sx={{
                ml: 1,
              }}
            >
              <Typography sx={{ fontWeight: 600 }}>
                Shift Schedule Assignment
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Allocate shifts to specific days of the week for each shift
                group
              </Typography>
            </Box>
            <Box>
              <Box sx={{ ml: 1 }}>
                {rotationBasedOn === "Shift" ? (
                  <ShiftAssignmentForShift
                    shiftError={shiftError}
                    setShiftError={setShiftError}
                    allGroupDetails={allGroupDetails}
                    shiftCycleDetails={shiftCycleDetails}
                    setShiftCycleDetails={setShiftCycleDetails}
                    setUpdateData={setUpdateData}
                  />
                ) : rotationBasedOn === "Weekdays" ? (
                  <ShiftAssignmentForWeekDays
                    WeekDaysData={WeekDaysData}
                    setWeekDaysData={setWeekDaysData}
                    setUpdateData={setUpdateData}
                    weekdayError={weekdayError}
                  />
                ) : null}
              </Box>
            </Box>

            <Box
              sx={{
                ml: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box>
                <CustomButton
                  actionName="Prev"
                  typeName="button"
                  actionFuntion={() => {
                    handleBack(0);
                  }}
                />
              </Box>
              <Box>
                <CustomButton
                  actionName="Next"
                  typeName="submit"
                  actionFuntion={() => {
                    validateWeekDaysShifts();
                  }}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  },
);
